import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Settings2, Gift, AlertTriangle, CheckCircle2, Power, MailCheck, MailQuestion, MailX, History, Cpu } from 'lucide-react';
import { puntovService, clientesService } from '../../../services/api';
import { useAuth } from '../../../hooks/useAuth';
import TablaDatos from './TablaDatos';
import DialogoConfigCobro from './DialogoConfigCobro';
import DialogoCortesia from './DialogoCortesia';
import DialogoHabilitarTodos from './DialogoHabilitarTodos';
import DialogoCorreoFacturacion from './DialogoCorreoFacturacion';
import DialogoHistorialCobros from './DialogoHistorialCobros';
import DialogoMaquinasCobro from './DialogoMaquinasCobro';
import { fmtUsd, fmtPeriodo } from './formato';

const ESTADO_COBRO = {
  activo: { etiqueta: 'Cobro activo', clase: 'text-green-700 bg-green-50 border-green-200' },
  pausado: { etiqueta: 'Sin cobro', clase: 'text-gray-600 bg-gray-50 border-gray-200' },
  sin_configurar: { etiqueta: 'Sin configurar', clase: 'text-amber-700 bg-amber-50 border-amber-200' }
};

const estadoCobro = (f) => (!f.cuenta ? 'sin_configurar' : f.cuenta.cobro_habilitado ? 'activo' : 'pausado');

const textoTarifa = (cuenta) => {
  if (!cuenta?.tarifa) return null;
  if (cuenta.tarifa.tipo === 'mixta') {
    return `${Number(cuenta.tarifa.valor).toLocaleString('es-VE')} % de las ventas; si no llega a ${fmtUsd(cuenta.tarifa.minimo)}, ${fmtUsd(cuenta.tarifa.fijo)} por máquina`;
  }
  return cuenta.tarifa.tipo === 'porcentaje'
    ? `${Number(cuenta.tarifa.valor).toLocaleString('es-VE')} % de las ventas`
    : `${fmtUsd(cuenta.tarifa.valor)} por máquina`;
};

const claveFila = (f) => f.base_datos;
const textoBusqueda = (f) => `${f.slug} ${f.base_datos} ${f.duenio.nombre} ${f.duenio.correo} ${f.erpNombre || ''}`;

// Dónde se decide a quién se le cobra la mensualidad de Punto V, con qué
// tarifa, y qué meses van en cortesía (no se cobran).
const CobroClientes = () => {
  const { user } = useAuth();
  const esAdmin = user?.rol === 'admin';

  const [filas, setFilas] = useState([]);
  const [cortesiasPorEmpresa, setCortesiasPorEmpresa] = useState({});
  const [tarifasPorDefecto, setTarifasPorDefecto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [configurando, setConfigurando] = useState(null);
  const [cortesiaPara, setCortesiaPara] = useState(null);
  const [historialDe, setHistorialDe] = useState(null);
  const [maquinasDe, setMaquinasDe] = useState(null);
  const [habilitandoTodos, setHabilitandoTodos] = useState(false);
  const [correoPara, setCorreoPara] = useState(null);
  const [cambiandoVerificacion, setCambiandoVerificacion] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [cli, cue, cor, cob, erp] = await Promise.all([
        puntovService.listarClientes(),
        puntovService.cuentasCobro(),
        puntovService.cortesias(),
        puntovService.resumenCobros().catch(() => ({ data: { empresas: [] } })),
        clientesService.getAll().catch(() => ({ data: [] }))
      ]);
      const cuentas = new Map((cue.data || []).map(c => [c.empresa, c]));
      setTarifasPorDefecto(cue.tarifas_por_defecto || null);
      const cobros = new Map((cob.data?.empresas || []).map(e => [e.empresa, e]));
      const nombresErp = new Map((erp.data || []).map(c => [c.id, [c.nombre, c.apellido].filter(Boolean).join(' ')]));

      const porEmpresa = {};
      (cor.data || []).forEach(c => { (porEmpresa[c.db_vending_principal] ||= []).push(c); });
      setCortesiasPorEmpresa(porEmpresa);

      setFilas((cli.data || [])
        .filter(c => !c.archivado && !c.es_cuenta_erp && cuentas.get(c.base_datos)?.tipo !== 'interna')
        .map(c => {
          const cuenta = cuentas.get(c.base_datos) || null;
          const cobro = cobros.get(c.base_datos);
          const adeudado = cobro && ['pendiente', 'recarga_cortada'].includes(cobro.situacion) && cobro.cobro && !cobro.cobro.pago
            ? cobro.cobro
            : null;
          return { ...c, cuenta, cobroPendiente: adeudado, erpNombre: cuenta?.erp_cliente_id ? nombresErp.get(cuenta.erp_cliente_id) : null };
        }));
    } catch (err) {
      setError(err.message || 'No se pudo cargar la configuración de cobro');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const guardarConfig = async (datos) => {
    await puntovService.guardarCuentaCobro(configurando.base_datos, datos);
    setAviso(`Cobro de ${configurando.slug} guardado: ${datos.cobro_habilitado ? 'se le cobrará la mensualidad' : 'no se le cobrará'}.`);
    setConfigurando(null);
    await cargar();
  };

  const pausadas = useMemo(() => filas.filter(f => estadoCobro(f) === 'pausado' && f.clasificacion?.tipo !== 'zona_propia'), [filas]);

  const habilitarTodos = async () => {
    const res = await puntovService.habilitarCobroTodas(pausadas.map(f => f.base_datos));
    const { habilitadas = 0, sin_cliente_erp: sinErp = [] } = res.data || {};
    setAviso(`Cobro activado en ${habilitadas} ${habilitadas === 1 ? 'cuenta' : 'cuentas'}.${sinErp.length ? ` Faltan por vincular al ERP: ${sinErp.map(b => b.replace(/^esgamanc_/, '')).join(', ')}.` : ''}`);
    setHabilitandoTodos(false);
    await cargar();
  };

  const verificarCorreo = async (correo) => {
    await puntovService.verificarCorreoFacturacion(correoPara.base_datos, correo);
    setAviso(`Correo de facturación de ${correoPara.slug} verificado: ${correo}.`);
    setCorreoPara(null);
    await cargar();
  };

  const alternarVerificacion = useCallback(async (f) => {
    const habilitada = !f.cuenta.correo_verificacion_habilitada;
    setAviso('');
    setError('');
    setCambiandoVerificacion(f.base_datos);
    try {
      await puntovService.habilitarVerificacionCorreo(f.base_datos, habilitada);
      setAviso(habilitada
        ? `Verificación de correo habilitada para ${f.slug}: el panel de Punto V le pedirá confirmar su correo de facturación.`
        : `Verificación de correo deshabilitada para ${f.slug}: el panel de Punto V ya no se la pedirá.`);
      await cargar();
    } catch (err) {
      setError(err.message || 'No se pudo cambiar la verificación de correo');
    } finally {
      setCambiandoVerificacion(null);
    }
  }, [cargar]);

  const otorgar = async (periodo, motivo) => {
    const res = await puntovService.otorgarCortesia(cortesiaPara.base_datos, periodo, motivo);
    const anulado = res.data?.cobro_anulado;
    const errorCotizacion = res.data?.cotizacion?.error;
    setAviso((anulado
      ? `Cortesía de ${fmtPeriodo(periodo)} para ${cortesiaPara.slug}: se anuló el cobro de ${fmtUsd(anulado.total_usd)}${res.data?.cotizacion?.numero ? ` y la cotización ${res.data.cotizacion.numero} quedó rechazada` : ''}.`
      : `Cortesía de ${fmtPeriodo(periodo)} para ${cortesiaPara.slug}: ese mes no se cobrará.`)
      + (errorCotizacion ? ` Atención: no se pudo rechazar la cotización (${errorCotizacion}); hay que rechazarla a mano.` : ''));
    setCortesiaPara(null);
    await cargar();
  };

  const revocar = async (c) => {
    await puntovService.revocarCortesia(c.id);
    setAviso(`Cortesía de ${fmtPeriodo(c.periodo)} revocada: ese mes se cobrará normalmente.`);
    await cargar();
  };

  useEffect(() => {
    if (!cortesiaPara) return;
    const actualizada = filas.find(f => f.base_datos === cortesiaPara.base_datos);
    if (actualizada && actualizada !== cortesiaPara) setCortesiaPara(actualizada);
  }, [filas, cortesiaPara]);

  const columnas = useMemo(() => [
    {
      id: 'cliente', titulo: 'Cliente', ordenable: true, valor: (f) => f.slug,
      render: (f) => (
        <>
          <div className="font-medium text-gray-800">{f.slug}</div>
          <div className="text-xs text-gray-400">{f.duenio.nombre}</div>
        </>
      )
    },
    {
      id: 'cobro', titulo: 'Cobro', ordenable: true, valor: estadoCobro,
      filtro: [
        { valor: 'activo', etiqueta: 'Cobro activo' },
        { valor: 'pausado', etiqueta: 'Sin cobro' },
        { valor: 'sin_configurar', etiqueta: 'Sin configurar' }
      ],
      render: (f) => {
        const e = ESTADO_COBRO[estadoCobro(f)];
        return <span className={`inline-flex px-2 py-0.5 rounded-full border text-xs font-medium whitespace-nowrap ${e.clase}`}>{e.etiqueta}</span>;
      }
    },
    {
      id: 'tarifa', titulo: 'Tarifa', valor: (f) => textoTarifa(f.cuenta) || '',
      render: (f) => (
        <>
          <div className="text-gray-700">{textoTarifa(f.cuenta) || <span className="text-gray-400">—</span>}</div>
          {f.cuenta?.tarifas_por_maquina > 0 && <div className="text-xs text-gray-400">+{f.cuenta.tarifas_por_maquina} por máquina</div>}
        </>
      )
    },
    {
      id: 'erp', titulo: 'Cliente ERP', valor: (f) => f.erpNombre || '',
      render: (f) => (f.erpNombre
        ? <span className="text-gray-700">{f.erpNombre}</span>
        : f.cuenta?.cobro_habilitado
          ? <span className="inline-flex items-center gap-1 text-xs text-amber-700"><AlertTriangle size={12} /> Sin vincular</span>
          : <span className="text-gray-400">—</span>)
    },
    {
      id: 'correo', titulo: 'Correo de facturación', valor: (f) => f.cuenta?.correo_facturacion || '',
      render: (f) => {
        if (!f.cuenta) return <span className="text-gray-400">—</span>;
        return (
          <>
            {f.cuenta.correo_facturacion
              ? <div className="text-gray-700 break-all">{f.cuenta.correo_facturacion}</div>
              : <div className="text-gray-400">Sin correo</div>}
            {f.cuenta.correo_facturacion && (f.cuenta.correo_verificado
              ? <span className="inline-flex items-center gap-1 text-xs text-green-700"><CheckCircle2 size={12} /> Verificado</span>
              : <span className="inline-flex items-center gap-1 text-xs text-amber-700"><AlertTriangle size={12} /> Sin verificar</span>)}
            {f.cuenta.correo_verificacion_habilitada && (
              <div className="inline-flex items-center gap-1 text-xs text-blue-700"><MailQuestion size={12} /> El cliente puede verificarlo</div>
            )}
          </>
        );
      }
    },
    {
      id: 'cortesias', titulo: 'Cortesías', valor: (f) => (cortesiasPorEmpresa[f.base_datos] || []).length,
      render: (f) => {
        const lista = cortesiasPorEmpresa[f.base_datos] || [];
        if (!lista.length) return <span className="text-gray-400">—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {lista.slice(0, 3).map(c => (
              <span key={c.id} title={c.motivo} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-green-50 text-green-700 text-xs">
                <CheckCircle2 size={11} /> {fmtPeriodo(c.periodo)}
              </span>
            ))}
            {lista.length > 3 && <span className="text-xs text-gray-400">+{lista.length - 3}</span>}
          </div>
        );
      }
    },
    {
      id: 'acciones', titulo: '', alinear: 'right', valor: () => '',
      render: (f) => (
        <div className="flex items-center justify-end gap-1.5">
          <button
            type="button"
            onClick={() => { setAviso(''); setHistorialDe(f); }}
            disabled={!f.cuenta}
            title={f.cuenta ? 'Meses pagados, adeudados y exonerados' : 'Primero configure el cobro'}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 border border-gray-200 rounded-lg hover:border-blue-300 hover:text-blue-700 disabled:opacity-40 whitespace-nowrap"
          >
            <History size={12} /> Historial
          </button>
          <button
            type="button"
            onClick={() => { setAviso(''); setMaquinasDe(f); }}
            disabled={!f.cuenta}
            title={f.cuenta ? 'Qué máquinas se cobran y cuánto' : 'Primero configure el cobro'}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 border border-gray-200 rounded-lg hover:border-blue-300 hover:text-blue-700 disabled:opacity-40 whitespace-nowrap"
          >
            <Cpu size={12} /> Máquinas
          </button>
          {esAdmin && (<>
          <button
            type="button"
            onClick={() => { setAviso(''); setConfigurando(f); }}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 border border-gray-200 rounded-lg hover:border-blue-300 hover:text-blue-700 whitespace-nowrap"
          >
            <Settings2 size={12} /> Configurar
          </button>
          <button
            type="button"
            onClick={() => { setAviso(''); setCorreoPara(f); }}
            disabled={!f.cuenta}
            title={f.cuenta ? 'Marcar el correo de facturación como verificado' : 'Primero configure el cobro'}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 border border-gray-200 rounded-lg hover:border-blue-300 hover:text-blue-700 disabled:opacity-40 whitespace-nowrap"
          >
            <MailCheck size={12} /> Verificar correo
          </button>
          <button
            type="button"
            onClick={() => alternarVerificacion(f)}
            disabled={!f.cuenta || cambiandoVerificacion === f.base_datos}
            title={!f.cuenta
              ? 'Primero configure el cobro'
              : f.cuenta.correo_verificacion_habilitada
                ? 'Dejar de pedirle al cliente que verifique su correo en el panel de Punto V'
                : 'Pedirle al cliente que verifique su correo en el panel de Punto V'}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 border border-gray-200 rounded-lg hover:border-blue-300 hover:text-blue-700 disabled:opacity-40 whitespace-nowrap"
          >
            {f.cuenta?.correo_verificacion_habilitada
              ? <><MailX size={12} /> Quitar verificación al cliente</>
              : <><MailQuestion size={12} /> Habilitar verificación al cliente</>}
          </button>
          <button
            type="button"
            onClick={() => { setAviso(''); setCortesiaPara(f); }}
            disabled={!f.cuenta}
            title={f.cuenta ? 'Dar un mes sin cobro' : 'Primero configure el cobro'}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 border border-gray-200 rounded-lg hover:border-green-300 hover:text-green-700 disabled:opacity-40 whitespace-nowrap"
          >
            <Gift size={12} /> Cortesía
          </button>
          </>)}
        </div>
      )
    }
  ], [esAdmin, cortesiasPorEmpresa, cambiandoVerificacion, alternarVerificacion]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-gray-500">
          A quién se le cobra la mensualidad, con qué tarifa, y qué meses van en cortesía.
        </p>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          {esAdmin && (
            <button
              onClick={() => { setAviso(''); setHabilitandoTodos(true); }}
              disabled={loading || pausadas.length === 0}
              title={pausadas.length === 0 ? 'No hay cuentas sin cobro' : 'Activar el cobro de todas las cuentas sin cobro'}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm disabled:opacity-50"
            >
              <Power size={16} /> Activar cobro a todos
            </button>
          )}
          <button onClick={cargar} disabled={loading} className="px-3 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm disabled:opacity-60">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Actualizar
          </button>
        </div>
      </div>

      {aviso && (
        <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl p-4 text-sm flex items-start gap-2">
          <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" /> <span>{aviso}</span>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" /> <span>{error}</span>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading && filas.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : (
          <TablaDatos
            columnas={columnas}
            filas={filas}
            clave={claveFila}
            textoBusqueda={textoBusqueda}
            placeholderBusqueda="Buscar cliente"
            vacio="No hay clientes con base en el servidor"
          />
        )}
      </div>

      {configurando && (
        <DialogoConfigCobro
          cliente={configurando}
          cuenta={configurando.cuenta}
          tarifasPorDefecto={tarifasPorDefecto}
          onGuardar={guardarConfig}
          onCancel={() => setConfigurando(null)}
        />
      )}
      {habilitandoTodos && (
        <DialogoHabilitarTodos
          slugs={pausadas.map(f => f.slug)}
          sinVincular={pausadas.filter(f => !f.cuenta.erp_cliente_id).map(f => f.slug)}
          onConfirmar={habilitarTodos}
          onCancel={() => setHabilitandoTodos(false)}
        />
      )}
      {correoPara && (
        <DialogoCorreoFacturacion
          cliente={correoPara}
          cuenta={correoPara.cuenta}
          onGuardar={verificarCorreo}
          onCancel={() => setCorreoPara(null)}
        />
      )}
      {maquinasDe && (
        <DialogoMaquinasCobro
          cliente={maquinasDe}
          puedeEditar={esAdmin}
          onClose={() => setMaquinasDe(null)}
          onCambio={cargar}
        />
      )}

      {historialDe && (
        <DialogoHistorialCobros
          cliente={historialDe}
          puedeExonerar={esAdmin}
          onClose={() => setHistorialDe(null)}
          onCambio={cargar}
        />
      )}

      {cortesiaPara && (
        <DialogoCortesia
          cliente={cortesiaPara}
          cobroPendiente={cortesiaPara.cobroPendiente}
          cortesias={cortesiasPorEmpresa[cortesiaPara.base_datos] || []}
          onOtorgar={otorgar}
          onRevocar={revocar}
          onCancel={() => setCortesiaPara(null)}
        />
      )}
    </div>
  );
};

export default CobroClientes;
